import React, { forwardRef } from 'react'
import { useCallback } from 'sb-hooks'

import type { State } from './MenuContext'


type MenuItemProps = {
  children: React.ReactNode
  className?: string
  _ref?: (node: HTMLElement) => void
  index: number
  role?: string
  state: State
  setState: (state: Partial<State>) => void
  onClick?: (event: React.MouseEvent) => void // TODO not used outside, should we add it? - added on 5/28/20 by pavelivanov
  onKeyDown?: (event: React.KeyboardEvent) => void // TODO not used outside, should we add it? - added on 5/28/20 by pavelivanov
  onSelect?: () => void
}

const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>((props, ref) => {
  const {
    _ref, index, role = 'menuitem',
    state, setState,
    onKeyDown, onSelect, onClick,
    ...rest
  } = props

  const isSelected = index === state.selectionIndex
  const dataSelected = role === 'menuitem' && isSelected ? true : undefined

  const setRef = useCallback((node) => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(node)
      }
      else {
        // @ts-ignore
        ref.current = node
      }
    }

    if (_ref) {
      _ref(node)
    }
  }, [ _ref, ref ]) // eslint-disable-line react-hooks/exhaustive-deps

  const select = useCallback(() => {
    if (typeof onSelect === 'function') {
      onSelect()
    }

    setState({
      isOpen: false,
      selectionIndex: -1,
      closingWithClick: false,
    })
  }, [ onSelect ]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = useCallback((event) => {
    if (typeof onClick === 'function') {
      onClick(event)
    }

    select()
  }, [ onClick, select ])

  const handleKeyDown = useCallback((event) => {
    if (typeof onKeyDown === 'function') {
      onKeyDown(event)
    }

    if (event.key === 'Enter') {
      // prevent the button from being "clicked" by
      // this "Enter" keydown
      event.preventDefault()
      select()
    }
  }, [ onKeyDown, select ])

  return (
    <div
      {...rest}
      ref={setRef}
      role={role}
      tabIndex={-1}
      data-selected={dataSelected}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    />
  )
})


export default MenuItem
